import type { Exercise } from '@/Common/ActivityFunctions';
import { writable } from 'svelte/store';
import { DataStatus } from './common';
import { debouncer } from '@/Util/utils';
import { getRecommendedTraining } from '@/Util/api';

/*
This singleton loads and manages the recommended training for a specific date and time.
- Object must be initialized once in an onMount function for reactivity to work, preferably in a parent component.
*/

export interface RecommendedTrainingWritable {
  status: DataStatus;
  date: Date;
  exercises: Exercise[];
}

export let hasSubscribers = false;

let currentDate: Date = null;
let loadCounter: number = 0;

function createSingleton() {
  const { subscribe, set, update } = writable<RecommendedTrainingWritable>({
    status: DataStatus.NEW,
    date: new Date(),
    exercises: [],
  }, () => {
    hasSubscribers = true;
    return () => {
      hasSubscribers = false;
    };
  });
  
  async function load(date: Date, loadNumber: number): Promise<void> {
    // If a newer load has been initiated, discard this one.
    if (loadNumber != loadCounter) {
      return;
    }

    const response = await getRecommendedTraining(date);
    const exercises = response.exercises;

    // If a newer load has been initiated, discard this one.
    if (loadNumber != loadCounter) {
      return;
    }
    
    console.log('Singleton: Loaded recommended training for date', date, exercises);
    set({
      status: DataStatus.READY,
      date: date,
      exercises: exercises,
    });
  }

  const loadDebouncer = debouncer(200);
  const executeLoadDebouncer = (date, loadNumber) => loadDebouncer.debounce(() => load(date, loadNumber));

  async function reactiveReload(data) {
    if (data.refresh_date && data.refresh_date != '') {
      const endOfDay = new Date(currentDate);
      endOfDay.setHours(23, 59, 59, 999);
      if (new Date(data.refresh_date) <= endOfDay) {
        update((prev) => {
          return {
            status: prev.status == DataStatus.LOADING ? DataStatus.LOADING : DataStatus.RESYNCING,
            date: prev.date,
            exercises: prev.exercises,
          };
        });

        executeLoadDebouncer(currentDate, ++loadCounter);
      }
    } else {
      update((prev) => {
        return {
          status: prev.status == DataStatus.LOADING ? DataStatus.LOADING : DataStatus.RESYNCING,
          date: prev.date,
          exercises: prev.exercises,
        };
      });

      executeLoadDebouncer(currentDate, ++loadCounter);
    }
  }

  async function init(date: Date) {
    let isNew = false;
    currentDate = date;
    update((prev) => {
      if (prev.status !== DataStatus.NEW) {
        return prev;
      }
      isNew = true;
      return {
        status: DataStatus.LOADING,
        date: date,
        exercises: [],
      };
    });
    // Only initialize once.
    if (!isNew) return;

    load(date, ++loadCounter);

    //@ts-ignore
    const Xert = window.Xert;
    Xert._userChannel.bind('schedule_activity_complete', reactiveReload);
    Xert._userChannel.bind('modify_activity_complete', reactiveReload);
    Xert._userChannel.bind('delete_activity_complete', reactiveReload);
    Xert._userChannel.bind('upload_activity_complete', reactiveReload);
    Xert._userChannel.bind('trickle_complete', reactiveReload);
    Xert._userChannel.bind('sessions', reactiveReload);
    Xert._userChannel.bind('xss_targets_updated', reactiveReload);
  }

  function changeDate(date: Date) {
    currentDate = date;
    update((prev) => {
      return {
        status: DataStatus.LOADING,
        date: date,
        exercises: prev.exercises,
      };
    });

    load(date, ++loadCounter);
  }

  return {
    subscribe,
    set,
    update,
    init,
    changeDate,
  };
}

export const recommendedTraining = createSingleton();
